// app/context/product-context.tsx
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useFetcher } from '@remix-run/react';
import {
  updateFilterSearchParams,
  clearFilterSearchParams,
  updateSortSearchParams,
} from '~/utils/search-params-helpers';
import { defaultFilterGroups } from '~/components/product-constants';
import type { FilterGroup } from '~/components/filter-sidebar';
import { sortProductsByEffectivePrice } from '~/utils/product-sorting';

interface ProductContextType {
  // Products and metadata
  products: any[];
  allProducts: any[];
  facets: any;
  total: number;
  page: number;

  // Filter state
  filters: FilterGroup[];
  sidebarOpen: boolean;
  selectedSort: string;
  searchTerm: string;

  // Loading state
  isLoading: boolean;

  // Actions
  handleOptionSelect: (groupTitle: string, optionLabel: string) => void;
  handleClearFilters: () => void;
  handleSortSelect: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleLoadMore: () => void;
  setSearchTerm: (term: string) => void;
  setSidebarOpen: (open: boolean) => void;
  handleShowProducts: () => void;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

interface ProductProviderProps {
  children: React.ReactNode;
  initialProducts: any[];
  initialFacets: any;
  initialTotal: number;
  initialPage: number;
  apiPath: string;
  categorySlug?: string;
}

export const ProductProvider: React.FC<ProductProviderProps> = ({
  children,
  initialProducts,
  initialFacets,
  initialTotal,
  initialPage,
  apiPath,
  categorySlug = '',
}) => {
  // URL parameters
  const [searchParams, setSearchParams] = useSearchParams();

  // State for products and metadata
  const [products, setProducts] = useState<any[]>(initialProducts);
  const [allProducts, setAllProducts] = useState<any[]>(initialProducts);
  const [facets, setFacets] = useState(initialFacets);
  const [total, setTotal] = useState(initialTotal);
  const [page, setPage] = useState(initialPage);

  // Filter state
  const [filters, setFilters] = useState<FilterGroup[]>(defaultFilterGroups);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState(searchParams.get('sort') || 'Sort by Popular');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Store the current state of products for comparison
  const prevProductsRef = useRef<any[]>([]);

  // Fetch more products (for "Load More" functionality)
  // Fixed fetchMoreProducts function for product-context.tsx
  const fetchMoreProducts = async (pageNum: number) => {
    setIsLoading(true);
    try {
      // Create a copy of the current search params
      const params = new URLSearchParams(searchParams.toString());
      params.set('page', pageNum.toString());

      // Ensure we have the full path to the API endpoint
      // This is the key fix - ensure we have the correct URL format
      const url = new URL(window.location.href);

      // Clear the search parameters from the current URL and set our own
      url.search = `?${params.toString()}`;

      // Log for debugging
      console.log(`Fetching more products from: ${url.toString()}`);

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error(`Error fetching products: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching more products:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize products when the initial data changes
  // Track searchParams to ensure updates when filters change (URL changes)
  useEffect(() => {
    setProducts(initialProducts);
    setAllProducts(initialProducts);
    setFacets(initialFacets);
    setTotal(initialTotal);
    setPage(initialPage);
    prevProductsRef.current = initialProducts;
  }, [initialProducts, initialFacets, initialTotal, initialPage, searchParams.toString()]);

  // Update filters based on facets and search params
  useEffect(() => {
    if (facets) {
      setFilters((prev) =>
        prev.map((group) => {
          if (group.title === 'Brand' && facets.brand) {
            return {
              ...group,
              options: facets.brand.map((item: any) => ({
                label: item.value,
                active: searchParams.get('brand') === item.value,
              })),
            };
          }
          if (group.title === 'Subtype' && facets.subtype) {
            return {
              ...group,
              options: facets.subtype.map((item: any) => ({
                label: item.value,
                active: searchParams.get('subtype') === item.value,
              })),
            };
          }
          if (group.title === 'Classification' && facets.classification) {
            return {
              ...group,
              options: facets.classification.map((item: any) => ({
                label: item.value,
                active: searchParams.get('classification') === item.value,
              })),
            };
          }
          if (group.title === 'Size' && facets.size) {
            return {
              ...group,
              options: facets.size.map((item: any) => ({
                label: item.value,
                active: searchParams.get('size') === item.value,
              })),
            };
          }

          if (group.title === 'Deal' && facets.discount_applied) {
            return {
              ...group,
              options: facets.discount_applied.map((item: any) => ({
                label: item.value,
                active: searchParams.get('discount_applied') === item.value,
              })),
            };
          }

          return group;
        }),
      );
    }
  }, [facets, searchParams]);

  // Handlers for filter actions
  const handleOptionSelect = (groupTitle: string, optionLabel: string) => {
    const newParams = updateFilterSearchParams(searchParams, groupTitle, optionLabel);
    setSearchParams(newParams);
  };

  const handleClearFilters = () => {
    navigate('/shop?open-filters=1');
    return false;
  };

  const handleSortSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSort(event.target.value);
    const newParams = updateSortSearchParams(searchParams, event.target.value);
    setSearchParams(newParams);
  };
  const navigate = useNavigate();

  // In your ProductProvider or where handleLoadMore is defined
  const handleLoadMore = () => {
    if (isLoading) return;

    const nextPage = page + 1;
    console.log(`Loading more products, page ${nextPage}`);

    // Create a copy of the current search params
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set('page', nextPage.toString());

    // Special handling for the index route
    const isIndexRoute = window.location.pathname === '/';
    const fetchPath = isIndexRoute
      ? `/?index&${newParams.toString()}`
      : `${window.location.pathname}?${newParams.toString()}`;

    // Use Remix's fetcher to load more data
    fetcher.load(fetchPath);

    // Update the current page
    setPage(nextPage);
  };

  const handleShowProducts = () => {
    setSidebarOpen(false);
  };

  const fetcher = useFetcher();

  useEffect(() => {
    if (fetcher.data && fetcher.state === 'idle') {
      // Type check the fetcher data
      if (
        fetcher.data &&
        typeof fetcher.data === 'object' &&
        'products' in fetcher.data &&
        Array.isArray((fetcher.data as any).products)
      ) {
        // Now it's safe to use products
        const newProducts = (fetcher.data as any).products;
        setAllProducts((prev) => {
          const combined = [...prev, ...newProducts];
          // 🚀 FIX: Re-sort entire combined list when sorting by price
          // This ensures products with lower sale prices appear before products with higher regular prices
          // even when loading additional pages
          if (selectedSort === 'Price (Low - High)') {
            return sortProductsByEffectivePrice(combined, 'asc');
          } else if (selectedSort === 'Price (High - Low)') {
            return sortProductsByEffectivePrice(combined, 'desc');
          }
          return combined;
        });
        setIsLoading(false);
      }
    } else if (fetcher.state === 'loading') {
      setIsLoading(true);
    }
  }, [fetcher.data, fetcher.state, selectedSort]);

  const contextValue: ProductContextType = {
    products,
    allProducts,
    facets,
    total,
    page,
    filters,
    sidebarOpen,
    selectedSort,
    searchTerm,
    isLoading,
    handleOptionSelect,
    handleClearFilters,
    handleSortSelect,
    handleLoadMore,
    setSearchTerm,
    setSidebarOpen,
    handleShowProducts,
  };

  return <ProductContext.Provider value={contextValue}>{children}</ProductContext.Provider>;
};

export const useProductContext = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProductContext must be used within a ProductProvider');
  }
  return context;
};
